import React, { useState } from 'react';
import { SxProps, Theme } from '@mui/material';
import Box from '@mui/material/Box';
import {
  getBrandfetchImageUrls,
  parseDomainForBrandfetch,
} from 'components/shared/brandfetch/helper';

type LogoLinkProps = {
  url: string | undefined;
  width?: number;
  sx?: SxProps<Theme>;
};

function BrandFetchLogoLinkImg({
  url,
  width,
  onError,
  ...props
}: {
  url: string;
  width: number;
  onError?: React.ReactEventHandler<HTMLImageElement>;
} & React.HTMLProps<HTMLImageElement>) {
  const brandfetchImageUrls = getBrandfetchImageUrls(url);
  const domain = brandfetchImageUrls?.domain ?? '';
  const iconUrl = brandfetchImageUrls?.iconUrl ?? '';
  const logoUrl = brandfetchImageUrls?.logoUrl ?? '';

  const [src, setSrc] = useState(iconUrl);

  return (
    <img
      alt={domain}
      src={src}
      style={{ width, padding: '4px' }}
      {...props}
      onError={(event) => {
        const isSrcIconUrl = src === iconUrl;

        // If the icon URL returns an error, try the logo URL
        if (isSrcIconUrl) {
          setSrc(logoUrl);

          return;
        }

        if (onError) {
          onError(event);
        }
      }}
    />
  );
}

// helper HOC to delay rendering of children until ClearbitLogo !isLoading
export function WithOptionalLogoLink({
  url,
  width = 42,
  children,
}: LogoLinkProps & React.PropsWithChildren) {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const onLoad = () => {
    setIsLoading(false);
  };

  const onError = () => {
    setHasError(true);
    setIsLoading(false);
  };

  const show =
    url !== undefined &&
    url !== null &&
    parseDomainForBrandfetch(url) !== '' &&
    !hasError;

  return (
    <>
      {show && (
        <Box alignItems="center" sx={{ display: 'flex', width }}>
          <BrandFetchLogoLinkImg
            url={url}
            width={width}
            onLoad={onLoad}
            onError={onError}
          />
        </Box>
      )}
      {!isLoading && <>{children}</>}
    </>
  );
}

export default function LogoLinkImg({ url, width = 48, sx }: LogoLinkProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const onLoad = () => {
    setIsLoading(false);
  };

  const onError = () => {
    setHasError(true);

    setIsLoading(false);
  };

  const show =
    url !== undefined &&
    url !== null &&
    parseDomainForBrandfetch(url) !== '' &&
    !hasError;

  const sxWithLoading = isLoading
    ? { display: 'none' }
    : { display: 'flex', width, ...sx };

  return (
    <>
      {show && (
        <Box alignItems="center" sx={sxWithLoading}>
          <BrandFetchLogoLinkImg
            url={url}
            width={width}
            onLoad={onLoad}
            onError={onError}
          />
        </Box>
      )}
    </>
  );
}
